import { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/components/auth-provider";
import { Review } from "@/types/types";
import { getPropertyReviews } from "@/lib/review-service";

interface PropertyReviewsProps {
  propertyId: string;
  landlordId: string;
}

export default function PropertyReviews({ propertyId }: PropertyReviewsProps) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  async function loadReviews() {
    try {
      setLoading(true);
      const propertyReviews = await getPropertyReviews(propertyId);
      setReviews(propertyReviews);
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setLoading(false);
    }
  }

  const averageRating =
    reviews.length > 0
      ? reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
      : 0;

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
        }`}
      />
    ));
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Reviews & Ratings</CardTitle>
            {user && (
              <Button onClick={() => setShowReviewForm(!showReviewForm)}>
                Write a Review
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Review Form */}
          {showReviewForm && user && (
            <div className="mb-6 p-4 border rounded-lg">
              <p className="text-muted-foreground">
                Review form will be available after lease completion.
              </p>
              <Separator className="mt-6" />
            </div>
          )}

          {/* Average Rating */}
          {reviews.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-4xl font-bold">
                    {averageRating.toFixed(1)}
                  </div>
                  <div className="flex gap-1 mt-1">
                    {renderStars(Math.round(averageRating))}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  Based on {reviews.length}{" "}
                  {reviews.length === 1 ? "review" : "reviews"}
                </div>
              </div>
            </div>
          )}

          {/* Reviews List */}
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading reviews...
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                No reviews yet. Be the first to review this property!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="border-b pb-4 last:border-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback>
                          {review.reviewerName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{review.reviewerName}</p>
                          {review.verified && (
                            <Badge variant="secondary" className="text-xs">
                              Verified Tenant
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-1 mt-1">
                          {renderStars(review.rating)}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  {review.title && (
                    <h4 className="font-medium mb-1">{review.title}</h4>
                  )}
                  <p className="text-muted-foreground">{review.comment}</p>
                  {review.images && review.images.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {review.images.map((image: string, idx: number) => (
                        <div
                          key={idx}
                          className="relative h-20 w-20 rounded-lg overflow-hidden"
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={image}
                            alt={`Review photo ${idx + 1}`}
                            className="object-cover w-full h-full"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
